import React from 'react';
import { Filter, X, Search } from 'lucide-react';
import { AgentCategory } from '../lib/supabase';

interface FilterSidebarProps {
  categories: AgentCategory[];
  selectedCategories: string[];
  selectedSources: string[];
  searchQuery: string;
  isOpen: boolean;
  onCategoryChange: (category: string) => void;
  onSourceChange: (source: string) => void;
  onSearchChange: (query: string) => void;
  onClose: () => void;
}

const SOURCES = [
  { id: 'github', name: 'GitHub', color: 'bg-gray-900' },
  { id: 'huggingface', name: 'Hugging Face', color: 'bg-yellow-500' },
  { id: 'google', name: 'Google', color: 'bg-blue-600' },
  { id: 'microsoft', name: 'Microsoft', color: 'bg-blue-500' },
  { id: 'langchain', name: 'LangChain', color: 'bg-green-600' },
];

export function FilterSidebar({
  categories,
  selectedCategories,
  selectedSources,
  searchQuery,
  isOpen,
  onCategoryChange,
  onSourceChange,
  onSearchChange,
  onClose,
}: FilterSidebarProps) {
  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-dark-900/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:sticky top-0 left-0 h-screen lg:h-auto w-[280px] bg-white/95 backdrop-blur-xl border-r border-dark-200/50 p-4 md:p-6 transform transition-transform duration-300 z-50 lg:z-0 lg:transform-none overflow-y-auto shadow-strong lg:shadow-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-dark-900">Filters</h2>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-dark-500 hover:text-dark-700 hover:bg-dark-50 rounded-lg transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-dark-700 mb-2">
            Search Agents
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name, description..."
              className="input-modern pl-10"
            />
          </div>
        </div>

        {/* Sources */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-dark-700 mb-3">Sources</h3>
          <div className="space-y-2">
            {SOURCES.map((source) => (
              <label key={source.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedSources.includes(source.id)}
                  onChange={() => onSourceChange(source.id)}
                  className="rounded border-dark-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0"
                />
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${source.color}`} />
                  <span className="text-sm text-dark-700 group-hover:text-dark-900 font-medium">
                    {source.name}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div>
          <h3 className="text-sm font-semibold text-dark-700 mb-3">Categories</h3>
          <div className="space-y-2">
            {categories.map((category) => (
              <label key={category.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.name)}
                  onChange={() => onCategoryChange(category.name)}
                  className="rounded border-dark-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0"
                />
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-sm text-dark-700 group-hover:text-dark-900 font-medium">
                    {category.name}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Clear Filters */}
        {(selectedCategories.length > 0 || selectedSources.length > 0 || searchQuery) && (
          <div className="mt-6 pt-6 border-t border-dark-200">
            <button
              onClick={() => {
                onSearchChange('');
                selectedCategories.forEach(cat => onCategoryChange(cat));
                selectedSources.forEach(src => onSourceChange(src));
              }}
              className="btn-secondary w-full text-sm"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </>
  );
}